import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, from, Observable, Subject, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';

import {GeoLibService} from '@src/app/services/geoLib/geo-lib.service';
import {ItemManagerService} from '@src/app/services/itemManager/item-manager.service';
import {
  IUserGuideEvent,
  IUserGuideStep,
  IUserGuideLocation,
} from './user-guide.model';
import {ServerConnectionService} from '@src/app/services/serverConnection/server-connection.service';

@Injectable({
  providedIn: 'root',
})
export class UserGuideService {
  private _location: IUserGuideLocation;
  private _isEnabled: boolean;

  showLoseEventProgressWarning: boolean;
  isDrawerOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  clickedElement$: Subject<EventTarget[]> = new Subject();
  refreshOverlay$: Subject<void> = new Subject();

  constructor(
    private httpClient: HttpClient,
    private geoLibService: GeoLibService,
    private itemManagerService: ItemManagerService,
    private serverConnection: ServerConnectionService
  ) {
    this._isEnabled =
      window.hasOwnProperty('IS_TUTORIAL_ENABLED') &&
      window['IS_TUTORIAL_ENABLED'];

    this.itemManagerService.isFastLineSignUp = this.isFastSignup;
    this.serverConnection.isFastSignup = this.isFastSignup;
  }

  set clickedElement(path: EventTarget[]) {
    this.clickedElement$.next(path);
  }

  get isEnabled(): boolean {
    return this._isEnabled;
  }

  /**
   * Indicates if the guest is going through play mode before
   * signing up.
   */
  get isFastSignup(): boolean {
    return (
      this.isEnabled && window.location.pathname.includes('fast-line-event')
    );
  }

  getFastLineEventDetails(): IUserGuideEvent {
    return window.hasOwnProperty('FASTLINE_EVENT') && window['FASTLINE_EVENT'];
  }

  isPlaceSet(): boolean {
    const event = this.getFastLineEventDetails();
    const isPlacedSet = event &&
      typeof event.center_lat !== 'undefined' && !!event.center_lat &&
      typeof event.center_lat !== 'undefined' && !!event.center_lat &&
      typeof event.geocoding_result !== 'undefined' && !!event.geocoding_result &&
      typeof event.location !== 'undefined' && !!event.location &&
      typeof event.country_code !== 'undefined' && !!event.country_code;

    return isPlacedSet;
  }

  getLatLngForEvent(): Observable<{
    lat: number;
    lng: number;
  }> {
    const event: IUserGuideEvent = this.getFastLineEventDetails();

    // Create an observable from a promise so we can use its operators
    const geocoderObs$ = (
      request: google.maps.GeocoderRequest
    ): Observable<google.maps.GeocoderResponse> => {
      return from(
        this.geoLibService.geocoder.geocode(request)
      ) as Observable<google.maps.GeocoderResponse>;
    };

    return geocoderObs$({
      address: event.location.trim(),
      componentRestrictions: {
        country: event.country_code,
      },
    }).pipe(
      catchError(err => {
        if (err.code === 'ZERO_RESULTS') {
          // If we can't get a specific location, show the selected country as a fallback
          return geocoderObs$({
            componentRestrictions: {
              country: event.country_code,
            },
          });
        }

        return err;
      }),
      map((response: google.maps.GeocoderResponse) => {
        const results = response.results;

        if (!results || results.length === 0) {
          // Request either failed or 0 results
          throwError('Unable to find location');
        }

        const location = results[0].geometry.location;

        return {
          lat: location.lat(),
          lng: location.lng(),
        };
      })
    );
  }

  getAddressFromLatLng(
    lat: number,
    lng: number
  ): Observable<IUserGuideLocation> {
    const geocoderObs$ = (
      request: google.maps.GeocoderRequest
    ): Observable<google.maps.GeocoderResponse> => {
      return from(
        this.geoLibService.geocoder.geocode(request)
      ) as Observable<google.maps.GeocoderResponse>;
    };

    return geocoderObs$({
      location: {
        lat,
        lng,
      },
    }).pipe(
      map((response: google.maps.GeocoderResponse) => {
        const results = response.results;

        if (!results || results.length === 0) {
          // Request either failed or 0 results
          throwError('Unable to find location');
        }

        const countryCode: string = results[0].address_components.find(
          component => {
            return component.types.includes('country');
          }
        ).short_name;

        return {
          address: results[0].formatted_address,
          countryCode,
        };
      }),
      tap((location: IUserGuideLocation) => {
        this._location = location;
      })
    );
  }

  save(
    username: string,
    password: string,
    firstName: string,
    lastName: string,
    productUpdates: boolean,
    termsAgreed: boolean
  ): Observable<void> {
    let mapObjects = this.itemManagerService.getAllItems().filter(i => i).map((i)=>{
      i.config.setGroupId(null);
      return i;
    }).map(i => i.config.asSimpleObject());

    const body = {
      user: {
        username,
        password,
        firstName,
        lastName,
        confirm: password,
        productUpdates,
        terms: termsAgreed,
      },
      event: this.getFastLineEventDetails(),
      mapObjects: mapObjects,
    };

    if (!this.isPlaceSet() && this._location) {
      body.event.location = this._location.address;
      body.event.country_code = this._location.countryCode;
    }

    return this.httpClient.post<void>(
      `${window['BASE_URL']}/site/signup-wizard-save`,
      body
    );
  }

  getElement(step: IUserGuideStep): HTMLElement | null {
    if (step.element.className) {
      return document.querySelector(step.element.className);
    }

    return document.querySelector(`[data-ug="${step.element.dataAttr}"]`);
  }
}
